import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const RecommendationCard = ({ recommendation }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-error bg-error-50 border-error-100';
      case 'medium':
        return 'text-warning bg-warning-50 border-warning-100';
      case 'recommended':
        return 'text-primary bg-primary-50 border-primary-100';
      default:
        return 'text-text-secondary bg-surface-secondary border-border';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return 'AlertCircle';
      case 'medium':
        return 'Clock';
      case 'recommended':
        return 'Info';
      default:
        return 'Circle';
    }
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'website':
        return 'Globe';
      case 'app':
        return 'Smartphone';
      case 'youtube':
        return 'Play';
      case 'emergency':
        return 'Phone';
      case 'checklist':
        return 'CheckSquare';
      default:
        return 'ExternalLink';
    }
  };

  return (
    <div className="card hover:shadow-elevated transition-all duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(recommendation.priority)}`}>
              <div className="flex items-center space-x-1">
                <Icon name={getPriorityIcon(recommendation.priority)} size={12} />
                <span className="capitalize">{recommendation.priority} Priority</span>
              </div>
            </div>
            <span className="text-sm text-text-secondary">{recommendation.timeframe}</span>
          </div>
          <h4 className="text-lg font-heading font-semibold text-text-primary mb-2">
            {recommendation.title}
          </h4>
          <p className="text-text-secondary mb-4">
            {recommendation.description}
          </p>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="ml-4 p-2 text-text-secondary hover:text-text-primary transition-colors duration-150"
        >
          <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={20} />
        </button>
      </div>

      {isExpanded && (
        <div className="space-y-6 pt-4 border-t border-border">
          {/* Action Steps */}
          <div>
            <h5 className="font-medium text-text-primary mb-3 flex items-center space-x-2">
              <Icon name="CheckSquare" size={16} />
              <span>Action Steps</span>
            </h5>
            <ul className="space-y-2">
              {recommendation.actions.map((action, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-text-secondary">{action}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          {recommendation.resources && recommendation.resources.length > 0 && (
            <div>
              <h5 className="font-medium text-text-primary mb-3 flex items-center space-x-2">
                <Icon name="BookOpen" size={16} />
                <span>Helpful Resources</span>
              </h5>
              <div className="space-y-2">
                {recommendation.resources.map((resource, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-surface-secondary rounded-lg">
                    <Icon name={getResourceIcon(resource.type)} size={16} color="var(--color-primary)" />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-text-primary">{resource.name}</span>
                      {resource.type === 'emergency' && (
                        <div className="text-xs text-error mt-1">Emergency Resource</div>
                      )}
                    </div>
                    <button className="text-primary hover:text-primary-700 transition-colors duration-150">
                      <Icon name="ExternalLink" size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Progress Tracking */}
          <div className="bg-surface-secondary rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-text-primary">Track Your Progress</span>
              <button className="text-primary hover:text-primary-700 transition-colors duration-150">
                <Icon name="Plus" size={16} />
              </button>
            </div>
            <p className="text-xs text-text-secondary">
              Mark completed actions and monitor your improvement over time.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecommendationCard;